import { LevelBuilder } from "./builder.ts";
import { EN_BOMBER, EN_HEN, EN_LASER, EN_METEOR } from "../entities/enemy.ts";
import { diagonalStream } from "./patterns.ts";
import type { Level, SpawnContext } from "./levelTypes.ts";

/**
 * LEVEL 2 — "VECTOR"
 * A swarm of hens and laser eyeballs, escalating act by act until several of
 * each hound you at once. The cast is strictly hens, laser eyeballs, meteors,
 * and the pink dot streams:
 *
 *   Act 1 — two hens open, hopping in over sparse drifting dots.
 *   Act 2 — the eyeballs arrive in pairs and lock crossing lanes while more
 *           hens pour in and meteors slice the diagonal.
 *   Act 3 — three eyeballs hold the flanks and center; the flock takes a
 *           breather around the halfway point before Act 4 brings them back.
 *   Act 4 — the swarm: a wall of eyeballs, hens hopping in pairs near the
 *           70-80% mark, and a thicker meteor stream over the densest streams.
 *   Boss  — a single bomber closes the level, hurling five-bomb volleys
 *           while the swarm drains away.
 *   Coda  — everything departs; the field drains before the clear banner.
 */
export function buildLevel2(): Level {
  const b = new LevelBuilder(2, "VECTOR", "Evasion+", 88);

  // L2 meteors: the angle window widens past Level 1's pure verticals —
  // anything from near-vertical out to 45 degrees, entering from the top.
  const meteor = (ctx: SpawnContext, speed: number): void => {
    const dir = ctx.rng.sign();
    const ang = ctx.rng.range(Math.PI * 0.06, Math.PI * 0.25); // tilt from vertical
    ctx.enemy(EN_METEOR, ctx.arenaW * ctx.rng.range(0.15, 0.85), -50, {
      vx: dir * Math.sin(ang) * speed,
      vy: Math.cos(ang) * speed,
    });
  };

  // An eyeball posting up at a fractional arena position, entering from the
  // nearest edge so it slides into its lane.
  const eyeball = (ctx: SpawnContext, fx: number, fy: number, life: number): void => {
    const fromLeft = fx < 0.5;
    ctx.enemy(EN_LASER, fromLeft ? -40 : ctx.arenaW + 40, ctx.arenaH * fy, {
      homeX: ctx.arenaW * fx,
      homeY: ctx.arenaH * fy,
      life,
    });
  };

  const hen = (ctx: SpawnContext, fromLeft: boolean, fy: number): void => {
    ctx.enemy(EN_HEN, fromLeft ? -30 : ctx.arenaW + 30, ctx.arenaH * fy, {});
  };

  // --- Act 1 (0-16s): sparse dots; two hens introduce themselves ---
  b.every(2, 15, 1.2, (ctx, t) => {
    const k = Math.round((t - 2) / 1.2);
    const x = (k * 97) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.6 : Math.PI * 0.4, 300, 11);
  });
  b.at(5, (ctx) => hen(ctx, true, 0.72));
  b.at(11, (ctx) => hen(ctx, false, 0.78));

  // --- Act 2 (16-38s): eyeballs arrive in a pair; more hens; meteors ---
  b.cue(16, "intensity:1");
  b.every(16, 37, 0.8, (ctx, t) => {
    const k = Math.round((t - 16) / 0.8);
    const x = (k * 113) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.62 : Math.PI * 0.38, 340, 11);
  });
  b.at(18, (ctx) => eyeball(ctx, 0.7, 0.32, 18));
  b.at(24, (ctx) => eyeball(ctx, 0.28, 0.24, 18));
  b.at(20, (ctx) => hen(ctx, true, 0.75));
  b.at(30, (ctx) => hen(ctx, false, 0.7));
  b.every(21, 36, 4, (ctx) => meteor(ctx, 280));

  // --- Act 3 (38-58s): three eyeballs hold flanks + center; hens keep coming ---
  b.cue(38, "intensity:2");
  b.every(38, 57, 0.55, (ctx, t) => {
    const k = Math.round((t - 38) / 0.55);
    const x = (k * 131) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.66 : Math.PI * 0.34, 370, 10);
  });
  b.at(39, (ctx) => eyeball(ctx, 0.22, 0.26, 17));
  b.at(44, (ctx) => eyeball(ctx, 0.78, 0.3, 16));
  b.at(51, (ctx) => eyeball(ctx, 0.5, 0.18, 15));
  // No hen around here — the halfway point gets a breather from the flock
  // before Act 4's swarm brings them back.
  b.at(51, (ctx) => hen(ctx, false, 0.74));
  b.every(40, 56, 3.5, (ctx) => meteor(ctx, 320));

  // --- Act 4 (58-73s): the swarm — a wall of eyeballs + a flock of hens ---
  b.cue(58, "intensity:3");
  b.every(58, 73, 0.35, (ctx, t) => {
    const k = Math.round((t - 58) / 0.35);
    const x = (k * 149) % ctx.arenaW;
    diagonalStream(ctx, x, -20, k % 2 ? Math.PI * 0.7 : Math.PI * 0.3, 400, 10);
  });
  b.at(58, (ctx) => eyeball(ctx, 0.5, 0.5, 15));
  b.at(61, (ctx) => eyeball(ctx, 0.18, 0.3, 14));
  b.at(64, (ctx) => eyeball(ctx, 0.82, 0.26, 11));
  b.at(59, (ctx) => hen(ctx, true, 0.72));
  // Near the 70-80% mark, two hens hop in together instead of the usual
  // one — and the meteors get denser through the same stretch.
  b.at(66, (ctx) => {
    hen(ctx, false, 0.78);
    hen(ctx, true, 0.68);
  });
  b.every(60, 72, 4, (ctx) => {
    meteor(ctx, 340);
    meteor(ctx, 340);
  });
  b.every(62, 70, 1.6, (ctx) => meteor(ctx, 330));

  // --- BOSS (70-84s): a single bomber closes the level. The swarm thins out
  // as it waddles on and unloads its satchel — pure dodging, since the sword
  // is still a level away. A riser builds through the waddle-in and the
  // drop hits right as the first five-bomb volley detonates. ---
  b.at(70, (ctx) => {
    ctx.enemy(EN_BOMBER, -40, ctx.arenaH * 0.5, { homeX: ctx.arenaW * 0.5 });
  });
  b.cue(71.5, "riser");
  b.cue(75.5, "drop");

  // --- Coda (84-88s): the last bombs drain while the music strips back ---
  b.cue(84, "phase:clear");
  return b.build();
}
